import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, Box, Typography, Autocomplete, TextField,
  CircularProgress, Chip, Alert, Divider, FormControl,
  InputLabel, Select, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { orderTransferService, type TransferPreview, type TransferResult } from '../../wms/services/orderTransferService';
import { smartWarehouseService } from '../../wms/services/smartWarehouseService';
import { useTranslation } from 'react-i18next';

interface WmsTransferModalProps {
  open: boolean;
  onClose: () => void;
  orders: { id: string; orderNumber: string; customerName: string; platform?: string; itemCount?: number }[];
  onTransferred: () => void;
}

interface SmartWarehouse {
  id: string;
  name: string;
}

const WmsTransferModal: React.FC<WmsTransferModalProps> = ({ open, onClose, orders, onTransferred }) => {
  const { t } = useTranslation('salesSystem');
  const [warehouses, setWarehouses] = useState<SmartWarehouse[]>([]);
  const [selectedWarehouse, setSelectedWarehouse] = useState<SmartWarehouse | null>(null);
  const [priority, setPriority] = useState(0);
  const [loading, setLoading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [results, setResults] = useState<TransferResult[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isBulk = orders.length > 1;

  useEffect(() => {
    if (!open) {
      setSelectedWarehouse(null);
      setPriority(0);
      setPreview(null);
      setResults(null);
      setError(null);
      return;
    }
    (async () => {
      try {
        const list = (await smartWarehouseService.getAllList()).map((w) => ({ id: w.id, name: w.warehouseName }));
        setWarehouses(list);
        if (list.length === 1) setSelectedWarehouse(list[0]);
      } catch { setWarehouses([]); }
    })();
  }, [open]);

  const handlePreview = useCallback(async () => {
    if (!selectedWarehouse || orders.length !== 1) return;
    setPreviewing(true);
    setError(null);
    try {
      const p = await orderTransferService.preview({
        ecommerceOrderId: orders[0].id,
        warehouseId: selectedWarehouse.id,
      });
      setPreview(p);
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || t('ecommerceWmsTransferModal.errors.previewFailed'));
    } finally {
      setPreviewing(false);
    }
  }, [selectedWarehouse, orders]);

  useEffect(() => {
    if (selectedWarehouse && orders.length === 1) {
      handlePreview();
    } else {
      setPreview(null);
    }
  }, [selectedWarehouse, orders.length, handlePreview]);

  const handleTransfer = async () => {
    if (!selectedWarehouse) return;
    setLoading(true);
    setError(null);
    try {
      if (isBulk) {
        const resp = await orderTransferService.transferBulk({
          ecommerceOrderIds: orders.map(o => o.id),
          warehouseId: selectedWarehouse.id,
          priority,
        });
        setResults(resp.data);
      } else {
        const resp = await orderTransferService.transfer({
          ecommerceOrderId: orders[0].id,
          warehouseId: selectedWarehouse.id,
          priority,
        });
        setResults([resp.data!]);
      }
      onTransferred();
    } catch (e: any) {
      setError(e.response?.data?.message || e.message || t('ecommerceWmsTransferModal.errors.transferFailed'));
    } finally {
      setLoading(false);
    }
  };

  const hasResults = results && results.length > 0;
  const allSuccess = hasResults && results.every(r => r.success);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {isBulk ? t('ecommerceWmsTransferModal.titleBulk', { count: orders.length }) : t('ecommerceWmsTransferModal.titleSingle')}
      </DialogTitle>
      <DialogContent dividers>
        {!hasResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Order info */}
            {!isBulk && orders[0] && (
              <Box>
                <Typography
                  component="div"
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                >
                  {t('ecommerceWmsTransferModal.order')}: <strong>{orders[0].orderNumber}</strong>
                  {orders[0].platform && (
                    <Chip label={orders[0].platform} size="small" />
                  )}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('ecommerceWmsTransferModal.customer')}: {orders[0].customerName}
                </Typography>
              </Box>
            )}

            {isBulk && (
              <Alert severity="info">
                {t('ecommerceWmsTransferModal.bulkInfo', { count: orders.length })}
              </Alert>
            )}

            <Divider />

            {/* Warehouse selection */}
            <Autocomplete
              options={warehouses}
              getOptionLabel={(o) => o.name}
              value={selectedWarehouse}
              onChange={(_, v) => setSelectedWarehouse(v)}
              renderInput={(params) => (
                <TextField {...params} label={t('ecommerceWmsTransferModal.fields.targetWarehouse')} size="small" />
              )}
            />
            {warehouses.length === 0 && (
              <Alert severity="warning">
                {t('ecommerceWmsTransferModal.noWarehouseWarning')}
              </Alert>
            )}

            <FormControl size="small" fullWidth>
              <InputLabel>{t('ecommerceWmsTransferModal.fields.priority')}</InputLabel>
              <Select value={priority} label={t('ecommerceWmsTransferModal.fields.priority')} onChange={e => setPriority(Number(e.target.value))}>
                <MenuItem value={0}>{t('ecommerceWmsTransferModal.priority.normal')}</MenuItem>
                <MenuItem value={1}>{t('ecommerceWmsTransferModal.priority.high')}</MenuItem>
                <MenuItem value={2}>{t('ecommerceWmsTransferModal.priority.urgent')}</MenuItem>
              </Select>
            </FormControl>

            {/* Preview (single order) */}
            {!isBulk && previewing && (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CircularProgress size={24} />
                <Typography variant="body2" sx={{ mt: 1 }}>{t('ecommerceWmsTransferModal.previewChecking')}</Typography>
              </Box>
            )}

            {!isBulk && preview && (
              <Box>
                <Typography variant="subtitle2" gutterBottom>{t('ecommerceWmsTransferModal.productMatching')}</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('ecommerceWmsTransferModal.table.ecommerceProduct')}</TableCell>
                      <TableCell>{t('ecommerceWmsTransferModal.table.inventoryMatch')}</TableCell>
                      <TableCell align="center">{t('ecommerceWmsTransferModal.table.method')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {preview.productMatches.map((m, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Typography variant="body2">{m.ecomItemName}</Typography>
                          {m.ecomSKU && (
                            <Typography variant="caption" color="text.secondary">SKU: {m.ecomSKU}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {m.isMatched ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                              <Typography variant="body2">{m.matchedProductName}</Typography>
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <WarningIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                              <Typography variant="body2" color="warning.main">{t('ecommerceWmsTransferModal.notMatched')}</Typography>
                            </Box>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={m.matchMethod} size="small" variant="outlined"
                            color={m.isMatched ? 'success' : 'default'} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {preview.unmatchedCount > 0 && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    {t('ecommerceWmsTransferModal.unmatchedWarning', { count: preview.unmatchedCount })}
                  </Alert>
                )}
              </Box>
            )}

            {error && <Alert severity="error">{error}</Alert>}
          </Box>
        )}

        {/* Results */}
        {hasResults && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {allSuccess ? (
              <Alert severity="success">
                {results.length === 1
                  ? t('ecommerceWmsTransferModal.successSingle', { wmsOrderNumber: results[0].wmsOrderNumber })
                  : t('ecommerceWmsTransferModal.successBulk', { count: results.filter(r => r.success).length })}
              </Alert>
            ) : (
              <Alert severity="warning">
                {t('ecommerceWmsTransferModal.partialResult', { success: results.filter(r => r.success).length, failed: results.filter(r => !r.success).length })}
              </Alert>
            )}

            {results.some(r => r.warnings && r.warnings.length > 0) && (
              <Alert severity="info">
                {results.filter(r => r.warnings).flatMap(r => r.warnings!).map((w, i) => (
                  <Typography key={i} variant="body2">• {w}</Typography>
                ))}
              </Alert>
            )}

            {results.some(r => !r.success) && (
              <Box>
                <Typography variant="subtitle2" color="error">{t('ecommerceWmsTransferModal.failedTransfers')}</Typography>
                {results.filter(r => !r.success).map((r, i) => (
                  <Typography key={i} variant="body2" color="error">• {r.orderId}: {r.error}</Typography>
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{hasResults ? t('ecommerceWmsTransferModal.actions.close') : t('ecommerceWmsTransferModal.actions.cancel')}</Button>
        {!hasResults && (
          <Button
            variant="contained"
            onClick={handleTransfer}
            disabled={!selectedWarehouse || loading}
            startIcon={loading ? <CircularProgress size={16} /> : undefined}
          >
            {loading ? t('ecommerceWmsTransferModal.actions.transferring') : isBulk ? t('ecommerceWmsTransferModal.actions.transferBulk', { count: orders.length }) : t('ecommerceWmsTransferModal.actions.transfer')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WmsTransferModal;
